import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    SelectChangeEvent
} from '@mui/material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '../../../config/api';
import './styles.css';

interface Slot {
    _id: string;
    date: string;
    startTime: string;
    product: {
        _id: string;
        name: string;
    };
    maxParticipants: number;
    status: string;
}

interface FormData {
    date: string;
    startTime: string;
    product: string;
    maxParticipants: number;
}

const AdminSlotsSummary = () => {
    const navigate = useNavigate();
    const [slots, setSlots] = useState<Slot[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
    const [formData, setFormData] = useState<FormData>({
        date: '',
        startTime: '',
        product: '',
        maxParticipants: 10
    });

    useEffect(() => {
        fetchSlots();
    }, []);

    const fetchSlots = async () => {
        try {
            const response = await api.get('/planning/slots/all');
            setSlots(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des créneaux:', error);
        }
    };

    const handleOpenDialog = (slot?: Slot) => {
        if (slot) {
            setSelectedSlot(slot);
            setFormData({
                date: slot.date,
                startTime: slot.startTime,
                product: slot.product._id,
                maxParticipants: slot.maxParticipants
            });
        } else {
            setSelectedSlot(null);
            setFormData({
                date: '',
                startTime: '',
                product: '',
                maxParticipants: 10
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedSlot(null);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            if (selectedSlot) {
                await api.put(`/planning/slots/${selectedSlot._id}`, formData);
            } else {
                await api.post('/planning/slots', formData);
            }
            handleCloseDialog();
            fetchSlots();
        } catch (error) {
            console.error('Erreur lors de la sauvegarde du créneau:', error);
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce créneau ?')) {
            try {
                await api.delete(`/planning/slots/${id}`);
                fetchSlots();
            } catch (error) {
                console.error('Erreur lors de la suppression du créneau:', error);
            }
        }
    };

    const handleSelectChange = (event: SelectChangeEvent<string>) => {
        setFormData({ ...formData, product: event.target.value });
    };

    return (
        <Container maxWidth="lg" className="admin-slots-summary">
            <Paper className="summary-paper">
                <Typography variant="h4" className="summary-title">
                    Résumé des créneaux
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/admin')}
                    className="back-button"
                >
                    Retour au tableau de bord
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleOpenDialog()}
                    className="add-button"
                >
                    Ajouter un créneau
                </Button>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Heure</TableCell>
                                <TableCell>Produit</TableCell>
                                <TableCell>Participants max</TableCell>
                                <TableCell>Statut</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {slots.map((slot) => (
                                <TableRow key={slot._id}>
                                    <TableCell>
                                        {format(new Date(slot.date), 'EEEE d MMMM yyyy', { locale: fr })}
                                    </TableCell>
                                    <TableCell>{slot.startTime}</TableCell>
                                    <TableCell>{slot.product.name}</TableCell>
                                    <TableCell>{slot.maxParticipants}</TableCell>
                                    <TableCell>{slot.status}</TableCell>
                                    <TableCell>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => handleOpenDialog(slot)}
                                        >
                                            Modifier
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={() => handleDelete(slot._id)}
                                        >
                                            Supprimer
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Dialog open={openDialog} onClose={handleCloseDialog}>
                    <DialogTitle>
                        {selectedSlot ? 'Modifier le créneau' : 'Ajouter un créneau'}
                    </DialogTitle>
                    <Box component="form" onSubmit={handleSubmit}>
                        <DialogContent>
                            <TextField
                                fullWidth
                                type="date"
                                label="Date"
                                value={formData.date}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setFormData({ ...formData, date: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                            />
                            <TextField
                                fullWidth
                                type="time"
                                label="Heure de début"
                                value={formData.startTime}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setFormData({ ...formData, startTime: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                            />
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Produit</InputLabel>
                                <Select
                                    value={formData.product}
                                    onChange={handleSelectChange}
                                >
                                    <MenuItem value="67f6af5eadee0e3acfa1e2a4">Baptême Enfants</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                type="number"
                                label="Nombre maximum de participants"
                                value={formData.maxParticipants}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setFormData({ ...formData, maxParticipants: parseInt(e.target.value) })}
                                margin="normal"
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog}>Annuler</Button>
                            <Button type="submit" variant="contained" color="primary">
                                {selectedSlot ? 'Modifier' : 'Ajouter'}
                            </Button>
                        </DialogActions>
                    </Box>
                </Dialog>
            </Paper>
        </Container>
    );
};

export default AdminSlotsSummary;